import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api, { getCompras, getCompra, deleteCompra, addPagoToCompra, getCuentas, getCxP, getCxPDetail, createMultiLetras } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, FileText, Receipt, Eye, CreditCard, PlusCircle, FileCheck, Zap, Package } from 'lucide-react';

const ESTADO_COLORS = {
  BORRADOR: 'bg-gray-100 text-gray-700',
  PENDIENTE: 'bg-yellow-100 text-yellow-700',
  PAGADO: 'bg-emerald-100 text-emerald-700',
  ANULADO: 'bg-red-100 text-red-700'
};

const RECEPCION_COLORS = {
  PENDIENTE: 'bg-orange-100 text-orange-700',
  PARCIAL: 'bg-blue-100 text-blue-700',
  COMPLETA: 'bg-emerald-100 text-emerald-700'
};

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const ComprasPage = ({ tipo }) => {
  const navigate = useNavigate();
  const { empresaActual } = useApp();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Modal de pagos vinculados
  const [pagosModalOpen, setPagosModalOpen] = useState(false);
  const [selectedCompra, setSelectedCompra] = useState(null);
  const [pagosVinculados, setPagosVinculados] = useState([]);
  const [loadingPagos, setLoadingPagos] = useState(false);
  
  // Modal agregar pago
  const [agregarPagoOpen, setAgregarPagoOpen] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [nuevoPago, setNuevoPago] = useState({
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    monto: '',
    ref_pago: ''
  });
  const [savingPago, setSavingPago] = useState(false);

  // Modal crear letras
  const [crearLetrasOpen, setCrearLetrasOpen] = useState(false);
  const [selectedCxP, setSelectedCxP] = useState(null);
  const [fechaGiro, setFechaGiro] = useState(new Date().toISOString().split('T')[0]);
  const [letrasForm, setLetrasForm] = useState([]);
  const [savingLetras, setSavingLetras] = useState(false);
  const [bancoPagoLetras, setBancoPagoLetras] = useState('');
  
  // Generación rápida de letras
  const [generadorConfig, setGeneradorConfig] = useState({
    cantidad: 3,
    intervaloDias: 30,
    prefijo: 'L001-'
  });
  
  // Modal recibir mercadería
  const [recepcionOpen, setRecepcionOpen] = useState(false);
  const [compraRecepcion, setCompraRecepcion] = useState(null);
  const [lineasRecepcion, setLineasRecepcion] = useState([]);
  const [savingRecepcion, setSavingRecepcion] = useState(false);
  
  const esFactura = tipo === 'FACTURA_PROVEEDOR';
  const titulo = esFactura ? 'Facturas de Proveedor' : 'Gastos';
  const Icon = esFactura ? FileText : Receipt;

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [comprasRes, cuentasRes] = await Promise.all([
        getCompras(empresaActual.id, tipo),
        getCuentas(empresaActual.id)
      ]);
      setCompras(comprasRes.data);
      setCuentas(cuentasRes.data.filter(c => c.activo));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, tipo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    navigate(`/finanzas/compras/nuevo?tipo=${tipo}`);
  };

  const handleEdit = (id) => {
    navigate(`/finanzas/compras/${id}/editar?tipo=${tipo}`);
  };

  const handleDelete = async (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteCompra(itemToDelete);
      toast.success('Registro eliminado');
      loadData();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  // Abrir modal para agregar pago
  const handleAgregarPago = (compra) => {
    setSelectedCompra(compra);
    const saldoPendiente = compra.total_me - (compra.total_pagado_me || 0);
    setNuevoPago({
      cuenta_id: cuentas[0]?.id?.toString() || '',
      medio_pago: 'TRANSFERENCIA',
      monto: saldoPendiente.toFixed(2),
      ref_pago: `Pago de ${compra.nro_doc_full || `Factura #${compra.id}`}`
    });
    setAgregarPagoOpen(true);
  };

  // Guardar nuevo pago
  const handleGuardarPago = async () => {
    if (!nuevoPago.cuenta_id || !nuevoPago.monto || parseFloat(nuevoPago.monto) <= 0) {
      toast.error('Complete los datos del pago');
      return;
    }

    setSavingPago(true);
    try {
      await addPagoToCompra(selectedCompra.id, {
        cuenta_id: parseInt(nuevoPago.cuenta_id),
        medio_pago: nuevoPago.medio_pago,
        monto: parseFloat(nuevoPago.monto),
        ref_pago: nuevoPago.ref_pago
      });
      toast.success('Pago registrado');
      setAgregarPagoOpen(false);
      loadData(); // Recargar datos
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPago(false);
    }
  };

  // Ver pagos vinculados
  const handleVerPagos = async (compra) => {
    setSelectedCompra(compra);
    setLoadingPagos(true);
    setPagosModalOpen(true);
    
    try {
      const res = await getCompra(compra.id);
      setPagosVinculados(res.data.pagos || []);
    } catch (error) {
      toast.error('Error al cargar pagos');
      setPagosVinculados([]);
    } finally {
      setLoadingPagos(false);
    }
  };

  // === FUNCIONES PARA LETRAS ===
  // Buscar CxP de una factura y abrir modal de letras
  const handleCrearLetras = async (compra) => {
    try {
      // Buscar la CxP asociada a esta factura
      const cxpRes = await getCxP(empresaActual.id);
      const cxp = cxpRes.data.find(c => c.compra_id === compra.id);
      
      if (!cxp) {
        toast.error('No se encontró CxP para esta factura');
        return;
      }

      // Obtener detalle con letras
      const detailRes = await getCxPDetail(cxp.id);
      setSelectedCxP(detailRes.data);
      setFechaGiro(new Date().toISOString().split('T')[0]);
      setBancoPagoLetras(cuentas[0]?.id?.toString() || '');
      setLetrasForm([{
        id: 1,
        nro_letra: '',
        fecha_venc: '',
        monto: detailRes.data.saldo_me.toFixed(2)
      }]);
      setCrearLetrasOpen(true);
    } catch (error) {
      toast.error('Error al cargar datos de CxP');
    }
  };

  const addLetraRow = () => {
    const newId = letrasForm.length > 0 ? Math.max(...letrasForm.map(l => l.id)) + 1 : 1;
    setLetrasForm([...letrasForm, { id: newId, nro_letra: '', fecha_venc: '', monto: '' }]);
  };

  const removeLetraRow = (id) => {
    if (letrasForm.length <= 1) return;
    setLetrasForm(letrasForm.filter(l => l.id !== id));
  };

  const updateLetraRow = (id, field, value) => {
    setLetrasForm(letrasForm.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // Generar letras automáticamente
  const generarLetrasAutomaticas = () => {
    if (!selectedCxP) return;
    
    const { cantidad, intervaloDias, prefijo } = generadorConfig;
    const cantNum = parseInt(cantidad) || 1;
    const intervalo = parseInt(intervaloDias) || 30;
    
    if (cantNum < 1 || cantNum > 24) {
      toast.error('La cantidad debe estar entre 1 y 24 letras');
      return;
    }
    
    const saldoDisponible = selectedCxP.saldo_me;
    const montoBase = Math.floor((saldoDisponible / cantNum) * 100) / 100;
    const resto = Math.round((saldoDisponible - (montoBase * cantNum)) * 100) / 100;
    
    const nuevasLetras = [];
    let fechaBase = new Date(fechaGiro);
    
    for (let i = 0; i < cantNum; i++) {
      const fechaVenc = new Date(fechaBase);
      fechaVenc.setDate(fechaVenc.getDate() + (intervalo * (i + 1)));
      
      const monto = i === cantNum - 1 ? (montoBase + resto) : montoBase;
      
      nuevasLetras.push({
        id: i + 1,
        nro_letra: `${prefijo}${String(i + 1).padStart(5, '0')}`,
        fecha_venc: fechaVenc.toISOString().split('T')[0],
        monto: monto.toFixed(2)
      });
    }
    
    setLetrasForm(nuevasLetras);
    toast.success(`${cantNum} letra(s) generada(s) automáticamente`);
  };

  const handleGuardarLetras = async () => {
    const letrasValidas = letrasForm.filter(l => l.nro_letra && l.fecha_venc && parseFloat(l.monto) > 0);
    if (letrasValidas.length === 0) {
      toast.error('Complete al menos una letra');
      return;
    }

    if (!bancoPagoLetras) {
      toast.error('Seleccione el banco donde se pagarán las letras');
      return;
    }

    const totalLetras = letrasValidas.reduce((sum, l) => sum + parseFloat(l.monto), 0);
    if (totalLetras > selectedCxP.saldo_me + 0.01) {
      toast.error('El total excede el saldo disponible');
      return;
    }

    setSavingLetras(true);
    try {
      await createMultiLetras({
        cxp_id: selectedCxP.id,
        fecha_giro: fechaGiro,
        cuenta_pago_id: parseInt(bancoPagoLetras),
        letras: letrasValidas.map(l => ({
          nro_letra: l.nro_letra,
          fecha_venc: l.fecha_venc,
          monto: parseFloat(l.monto)
        }))
      });
      toast.success(`${letrasValidas.length} letra(s) creada(s)`);
      setCrearLetrasOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear letras');
    } finally {
      setSavingLetras(false);
    }
  };

  const totalLetrasForm = letrasForm.reduce((sum, l) => sum + (parseFloat(l.monto) || 0), 0);

  // Funciones para recepción de mercadería
  const handleRecibirMercaderia = async (compra) => {
    setCompraRecepcion(compra);
    try {
      // Obtener líneas de la compra
      const res = await api.get(`/compras/${compra.id}`);
      const lineas = res.data.lineas || [];
      setLineasRecepcion(lineas.map(l => ({
        linea_id: l.id,
        articulo: l.articulo_nombre || l.descripcion,
        cantidad_total: l.cant,
        cantidad_recibida: l.cantidad_recibida || 0,
        cantidad_pendiente: l.cant - (l.cantidad_recibida || 0),
        cantidad_recibir: l.cant - (l.cantidad_recibida || 0) // Por defecto, recibir todo lo pendiente
      })));
      setRecepcionOpen(true);
    } catch (error) {
      toast.error('Error al cargar líneas de la compra');
    }
  };

  const handleConfirmRecepcion = async () => {
    if (!compraRecepcion) return;
    
    const lineasARecibir = lineasRecepcion
      .filter(l => l.cantidad_recibir > 0)
      .map(l => ({ linea_id: l.linea_id, cantidad: l.cantidad_recibir }));
    
    if (lineasARecibir.length === 0) {
      toast.error('Ingresa al menos una cantidad a recibir');
      return;
    }
    
    setSavingRecepcion(true);
    try {
      const res = await api.post(`/compras/${compraRecepcion.id}/recibir`, { lineas: lineasARecibir });
      toast.success(res.data.mensaje);
      setRecepcionOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar recepción');
    } finally {
      setSavingRecepcion(false);
    }
  };

  // Calcular totales
  const totalPendiente = compras.filter(c => c.estado === 'PENDIENTE').reduce((sum, c) => sum + (c.total_pen || 0), 0);
  const totalPagado = compras.filter(c => c.estado === 'PAGADO').reduce((sum, c) => sum + (c.total_pen || 0), 0);

  if (!empresaActual) {
    return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{titulo}</h1>
            <div className="flex gap-4 text-sm text-gray-500">
              {esFactura && <span>Pendiente: S/ {totalPendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>}
              <span>Pagado: S/ {totalPagado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
        <Button onClick={handleNew} data-testid="new-compra-btn" className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          {esFactura ? 'Nueva Factura' : 'Nuevo Gasto'}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : compras.length === 0 ? (
            <div className="text-center py-12">
              <Icon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No hay {esFactura ? 'facturas' : 'gastos'} registrados</p>
              <Button onClick={handleNew} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Crear {esFactura ? 'primera factura' : 'primer gasto'}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Nro. Doc</TableHead>
                  <TableHead>Proveedor/Beneficiario</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <CreditCard className="w-4 h-4" />
                      Pagado
                    </div>
                  </TableHead>
                  <TableHead>Estado</TableHead>
                  {esFactura && <TableHead>Recepción</TableHead>}
                  {esFactura && <TableHead className="text-right">Saldo CxP</TableHead>}
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {compras.map((item) => {
                  const saldoPendiente = item.total_me - (item.total_pagado_me || 0);
                  const pagadoCompleto = saldoPendiente <= 0.01;
                  const tienePagos = item.total_pagado_me > 0;
                  
                  return (
                    <TableRow key={item.id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleEdit(item.id)}>
                      <TableCell>{item.fecha}</TableCell>
                      <TableCell className="font-medium">{item.nro_doc_full || '-'}</TableCell>
                      <TableCell>{item.proveedor_nombre || item.beneficiario_txt || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {item.moneda_codigo} {item.total_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {tienePagos ? (
                          <button
                            onClick={() => handleVerPagos(item)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors ${
                              pagadoCompleto ? 'text-emerald-600 font-medium' : 'text-orange-600'
                            }`}
                            title="Ver pagos vinculados"
                          >
                            <CreditCard className="w-3 h-3" />
                            {item.moneda_codigo} {item.total_pagado_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                          </button>
                        ) : esFactura && item.estado === 'PENDIENTE' && item.cxp_estado !== 'CANJEADO' ? (
                          <button
                            onClick={() => handleAgregarPago(item)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-emerald-50 text-emerald-600 transition-colors"
                            title="Agregar pago"
                          >
                            <PlusCircle className="w-3 h-3" />
                            Pagar
                          </button>
                        ) : esFactura && item.cxp_estado === 'CANJEADO' ? (
                          <span className="text-xs text-purple-600 font-medium">Canjeada</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={ESTADO_COLORS[item.estado]}>{item.estado}</Badge>
                      </TableCell>
                      {esFactura && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {item.estado_recepcion && (
                              <Badge className={RECEPCION_COLORS[item.estado_recepcion] || 'bg-gray-100'}>
                                {item.estado_recepcion}
                              </Badge>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRecibirMercaderia(item)} 
                              className={`text-xs ${item.estado_recepcion === 'COMPLETA' ? 'text-emerald-600' : 'text-orange-600'}`}
                              title={item.estado_recepcion === 'COMPLETA' ? 'Ver Recepción' : 'Recibir Mercadería'}
                            >
                              <Package className="w-3.5 h-3.5 mr-1" />
                              {item.estado_recepcion === 'COMPLETA' ? 'Ver' : 'Recibir'}
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      {esFactura && (
                        <TableCell className={`text-right font-medium ${item.cxp_saldo > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                          {item.cxp_saldo > 0 ? `S/ ${item.cxp_saldo.toLocaleString('es-PE', { minimumFractionDigits: 2 })}` : '-'}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {/* Botón Canjear por Letras - solo facturas pendientes con saldo y NO canjeadas */}
                          {esFactura && item.estado === 'PENDIENTE' && item.cxp_estado !== 'CANJEADO' && saldoPendiente > 0.01 && (
                            <Button variant="ghost" size="sm" onClick={() => handleCrearLetras(item)} title="Canjear por Letras" className="text-blue-600">
                              <FileCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item.id)} title="Ver/Editar">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} title="Eliminar">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Pagos Vinculados */}
      <Dialog open={pagosModalOpen} onOpenChange={setPagosModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Pagos Vinculados
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompra && (
            <div className="space-y-4">
              {/* Info del documento */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{selectedCompra.nro_doc_full || `${esFactura ? 'Factura' : 'Gasto'} #${selectedCompra.id}`}</p>
                    <p className="text-sm text-gray-500">{selectedCompra.proveedor_nombre || selectedCompra.beneficiario_txt}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="font-bold text-lg">{selectedCompra.moneda_codigo} {selectedCompra.total_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>

              {/* Lista de pagos */}
              {loadingPagos ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                </div>
              ) : pagosVinculados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No hay pagos vinculados
                </div>
              ) : (
                <div className="space-y-2">
                  {pagosVinculados.map((pago, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{pago.fecha}</span>
                          <Badge variant="outline" className="text-xs">{pago.medio_pago}</Badge>
                        </div>
                        <p className="text-sm text-gray-500">{pago.cuenta_nombre}</p>
                        {pago.ref_pago && (
                          <p className="text-xs text-gray-400">Ref: {pago.ref_pago}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">
                          {pago.moneda_codigo} {(pago.monto_aplicado_me || pago.monto_me)?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Resumen */}
              {pagosVinculados.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Total Pagado</span>
                    <span className="font-bold text-lg text-emerald-600">
                      S/. {pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {selectedCompra.total_me - selectedCompra.total_pagado_me > 0.01 && (
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-orange-600">Pendiente</span>
                      <span className="font-medium text-orange-600">
                        S/. {(selectedCompra.total_me - selectedCompra.total_pagado_me).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Botón agregar pago en modal de ver pagos (solo si hay saldo pendiente) */}
              {esFactura && selectedCompra && selectedCompra.total_me - (selectedCompra.total_pagado_me || 0) > 0.01 && (
                <div className="border-t pt-4 mt-4">
                  <Button 
                    onClick={() => {
                      setPagosModalOpen(false);
                      handleAgregarPago(selectedCompra);
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Agregar Pago
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Agregar Pago */}
      <Dialog open={agregarPagoOpen} onOpenChange={setAgregarPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600" />
              Agregar Pago
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompra && (
            <div className="space-y-4">
              {/* Info del documento */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{selectedCompra.nro_doc_full || `Factura #${selectedCompra.id}`}</p>
                    <p className="text-sm text-gray-500">{selectedCompra.proveedor_nombre || selectedCompra.beneficiario_txt}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Saldo Pendiente</p>
                    <p className="font-bold text-lg text-orange-600">
                      {selectedCompra.moneda_codigo} {(selectedCompra.total_me - (selectedCompra.total_pagado_me || 0)).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Formulario de pago */}
              <div className="space-y-4">
                <div>
                  <Label>Cuenta *</Label>
                  <Select value={nuevoPago.cuenta_id} onValueChange={(v) => setNuevoPago({...nuevoPago, cuenta_id: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccione cuenta" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuentas.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.nombre} ({c.moneda_codigo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Medio de Pago *</Label>
                  <Select value={nuevoPago.medio_pago} onValueChange={(v) => setNuevoPago({...nuevoPago, medio_pago: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDIOS_PAGO.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="mt-1"
                    value={nuevoPago.monto}
                    onChange={(e) => setNuevoPago({...nuevoPago, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label>Referencia</Label>
                  <Input
                    className="mt-1"
                    value={nuevoPago.ref_pago}
                    onChange={(e) => setNuevoPago({...nuevoPago, ref_pago: e.target.value})}
                    placeholder="Nro. de operación, cheque, etc."
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAgregarPagoOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarPago} 
              disabled={savingPago}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPago ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Letras */}
      <Dialog open={crearLetrasOpen} onOpenChange={setCrearLetrasOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              Canjear por Letras
            </DialogTitle>
          </DialogHeader>
          
          {selectedCxP && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Documento</p>
                    <p className="font-medium">{selectedCxP.nro_doc_full || `CxP #${selectedCxP.id}`}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Proveedor</p>
                    <p className="font-medium">{selectedCxP.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Saldo Disponible</p>
                    <p className="font-bold text-lg text-orange-600">
                      {selectedCxP.moneda_codigo} {selectedCxP.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Letras existentes */}
              {selectedCxP.letras && selectedCxP.letras.length > 0 && (
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-800 mb-3">Letras Existentes ({selectedCxP.letras.length})</h4>
                  <div className="space-y-2">
                    {selectedCxP.letras.map(letra => (
                      <div key={letra.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="font-mono font-medium text-blue-700">{letra.nro_letra}</span>
                          <span className="text-sm text-gray-500">Vence: {letra.fecha_venc}</span>
                        </div>
                        <span className="font-bold">{selectedCxP.moneda_codigo} {letra.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCxP.saldo_me > 0.01 && (
                <>
                  {/* Generación Rápida */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-800">Generación Rápida</h4>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-gray-600">Prefijo</Label>
                        <Input
                          value={generadorConfig.prefijo}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, prefijo: e.target.value})}
                          placeholder="L001-"
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          value={generadorConfig.cantidad}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, cantidad: e.target.value})}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Intervalo (días)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={generadorConfig.intervaloDias}
                          onChange={(e) => setGeneradorConfig({...generadorConfig, intervaloDias: e.target.value})}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={generarLetrasAutomaticas}
                          className="h-9 w-full bg-purple-600 hover:bg-purple-700"
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Generar
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Divide {selectedCxP.moneda_codigo} {selectedCxP.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })} en {generadorConfig.cantidad} letras = {selectedCxP.moneda_codigo} {(selectedCxP.saldo_me / (parseInt(generadorConfig.cantidad) || 1)).toLocaleString('es-PE', { minimumFractionDigits: 2 })} c/u
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fecha de Giro</Label>
                      <Input type="date" value={fechaGiro} onChange={(e) => setFechaGiro(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Banco donde se pagarán las letras *</Label>
                      <Select value={bancoPagoLetras} onValueChange={setBancoPagoLetras}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentas.map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.nombre} ({c.moneda_codigo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Letras a Crear</Label>
                      <Button variant="outline" size="sm" onClick={addLetraRow}>
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>N° Letra *</TableHead>
                            <TableHead>Fecha Venc. *</TableHead>
                            <TableHead>Monto *</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {letrasForm.map(letra => (
                            <TableRow key={letra.id}>
                              <TableCell>
                                <Input value={letra.nro_letra} onChange={(e) => updateLetraRow(letra.id, 'nro_letra', e.target.value)} placeholder="L001-00001" className="h-8" />
                              </TableCell>
                              <TableCell>
                                <Input type="date" value={letra.fecha_venc} onChange={(e) => updateLetraRow(letra.id, 'fecha_venc', e.target.value)} className="h-8" />
                              </TableCell>
                              <TableCell>
                                <Input type="number" step="0.01" value={letra.monto} onChange={(e) => updateLetraRow(letra.id, 'monto', e.target.value)} className="h-8 text-right" />
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => removeLetraRow(letra.id)} disabled={letrasForm.length <= 1} className="h-8 w-8 p-0 text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg flex justify-between">
                      <span>Total Letras</span>
                      <span className={`font-bold ${totalLetrasForm > selectedCxP.saldo_me ? 'text-red-600' : 'text-blue-600'}`}>
                        {selectedCxP.moneda_codigo} {totalLetrasForm.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearLetrasOpen(false)}>Cancelar</Button>
            {selectedCxP?.saldo_me > 0.01 && (
              <Button onClick={handleGuardarLetras} disabled={savingLetras || totalLetrasForm > (selectedCxP?.saldo_me || 0)} className="bg-blue-600 hover:bg-blue-700">
                {savingLetras ? 'Guardando...' : 'Crear Letras'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar este registro?
            </p>
            <p className="text-sm text-red-500 mt-2">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Recibir/Ver Mercadería */}
      <Dialog open={recepcionOpen} onOpenChange={setRecepcionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${compraRecepcion?.estado_recepcion === 'COMPLETA' ? 'text-emerald-600' : 'text-orange-600'}`}>
              <Package className="w-5 h-5" />
              {compraRecepcion?.estado_recepcion === 'COMPLETA' ? 'Detalle de Recepción' : 'Recibir Mercadería'}
            </DialogTitle>
          </DialogHeader>
          
          {compraRecepcion && (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 border ${compraRecepcion.estado_recepcion === 'COMPLETA' ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-sm font-medium ${compraRecepcion.estado_recepcion === 'COMPLETA' ? 'text-emerald-800' : 'text-orange-800'}`}>
                      Factura: {compraRecepcion.nro_doc_full}
                    </p>
                    <p className={`text-sm ${compraRecepcion.estado_recepcion === 'COMPLETA' ? 'text-emerald-700' : 'text-orange-700'}`}>
                      Proveedor: {compraRecepcion.proveedor_nombre}
                    </p>
                  </div>
                  {compraRecepcion.estado_recepcion && (
                    <Badge className={RECEPCION_COLORS[compraRecepcion.estado_recepcion]}>
                      {compraRecepcion.estado_recepcion}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Recibido</TableHead>
                      <TableHead className="text-right">Pendiente</TableHead>
                      {compraRecepcion.estado_recepcion !== 'COMPLETA' && (
                        <TableHead className="text-right">Recibir Ahora</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineasRecepcion.map((linea, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{linea.articulo}</TableCell>
                        <TableCell className="text-right">{linea.cantidad_total}</TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">{linea.cantidad_recibida}</TableCell>
                        <TableCell className={`text-right ${linea.cantidad_pendiente > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                          {linea.cantidad_pendiente > 0 ? linea.cantidad_pendiente : '-'}
                        </TableCell>
                        {compraRecepcion.estado_recepcion !== 'COMPLETA' && (
                          <TableCell className="text-right">
                            <Input 
                              type="number" 
                              step="0.01"
                              min="0"
                              max={linea.cantidad_pendiente}
                              value={linea.cantidad_recibir}
                              onChange={(e) => {
                                const nuevas = [...lineasRecepcion];
                                nuevas[idx].cantidad_recibir = parseFloat(e.target.value) || 0;
                                setLineasRecepcion(nuevas);
                              }}
                              className="w-24 h-8 text-right"
                              disabled={linea.cantidad_pendiente <= 0}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {compraRecepcion.estado_recepcion === 'COMPLETA' ? (
                <p className="text-xs text-emerald-600 font-medium">
                  ✓ Toda la mercadería ha sido recibida e ingresada al inventario.
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  Al confirmar, se creará automáticamente un ingreso de inventario y se actualizará el stock.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecepcionOpen(false)}>
              {compraRecepcion?.estado_recepcion === 'COMPLETA' ? 'Cerrar' : 'Cancelar'}
            </Button>
            {compraRecepcion?.estado_recepcion !== 'COMPLETA' && (
              <Button onClick={handleConfirmRecepcion} disabled={savingRecepcion} className="bg-orange-600 hover:bg-orange-700">
                {savingRecepcion ? 'Procesando...' : 'Confirmar Recepción'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComprasPage;
